import { Injectable } from '@angular/core';
import { maxBy } from 'lodash';
import { ConversationResponse } from '../../common/models/conversation-response.models';
import { CommunicationType } from '../../common/models/communication-type.enum';
import { EmailConversation } from '../models/email-conversation.model';
import { ConversationConverterService } from '../../common/services/conversation-converter-base.service';
import { Email } from '../models/email.model';
import { Participant } from '../../common/models/participant.model';
import { ConnectionState } from '../../common/models/connection-state.enum';

@Injectable({
  providedIn: 'root'
})
export class EmailConversationConverterService extends ConversationConverterService {
  convert(conversationResponse: ConversationResponse, agentUserId: string): EmailConversation {
    const agentParticipant = this.getAgentParticipant(conversationResponse, agentUserId);
    const customerParticipant =
      this.getCustomerParticipant(conversationResponse) || this.getExternalParticipant(conversationResponse);

    return {
      ...conversationResponse,
      isActive:
        this.hasActiveParticipantMessage(agentParticipant) ||
        this.hasUnfilledWrapup(agentParticipant) ||
        this.hasTransmittingParticipantMessage(agentParticipant),
      isAcceptable: this.hasAlertingParticipantEmail(agentParticipant),
      customerParticipant,
      agentParticipant,
      communicationType: CommunicationType.Email,
      acdParticipants: this.getAcdParticipants(conversationResponse),
      lastEmailMessageId: this.getLatestEmailFromConversation(agentParticipant, customerParticipant)?.messageId,
      isProactiveEmail: !this.getCustomerParticipant(conversationResponse)
    };
  }

  protected hasUnfilledWrapup(participant: Participant): boolean {
    return participant.wrapupRequired && !participant.wrapup;
  }

  private hasAlertingParticipantEmail(participant: Participant): boolean {
    return participant.emails?.some((pm) => pm.state.toLowerCase() === ConnectionState.Alerting);
  }

  private hasActiveParticipantMessage(participant: Participant): boolean {
    return participant.emails?.some((pm) =>
      ConversationConverterService.activeParticipantMessageTypes.has(pm.state.toLowerCase())
    );
  }

  private hasTransmittingParticipantMessage(participant: Participant): boolean {
    return participant.emails?.some((pm) => pm.state.toLowerCase() === ConnectionState.Transmitting);
  }

  private getLatestEmailFromConversation(agentParticipant: Participant, customerParticipant: Participant): Email {
    const agentEmails = agentParticipant?.emails || [];
    const customerEmails = customerParticipant?.emails || [];

    const emails = [...agentEmails, ...customerEmails].filter((e) => e.messageId);

    const maxConnectedTimeEmail = maxBy(emails, (e) => e.connectedTime);
    const maxDisconnectedTimeEmail = maxBy(emails, (e) => e.disconnectedTime);

    if (!maxConnectedTimeEmail) {
      return maxDisconnectedTimeEmail;
    }

    if (!maxDisconnectedTimeEmail) {
      return maxConnectedTimeEmail;
    }

    return maxConnectedTimeEmail.connectedTime > maxDisconnectedTimeEmail.disconnectedTime
      ? maxConnectedTimeEmail
      : maxDisconnectedTimeEmail;
  }
}
